import { defineComponent, useTemplateRef, ref, watch, computed, createBlock, openBlock, unref, withCtx, createElementBlock, createCommentVNode, createVNode, toDisplayString } from "vue";
import { setGuestNickname } from "@nextcloud/auth";
import { getBuilder } from "@nextcloud/browser-storage";
import NcDialog from "@nextcloud/vue/components/NcDialog";
import NcNoteCard from "@nextcloud/vue/components/NcNoteCard";
import NcTextField from "@nextcloud/vue/components/NcTextField";
import { t, l as logger, s as showError } from "./index-BYub6rWN.mjs";
import { validateFilename, InvalidFilenameError, InvalidFilenameErrorReason } from "@nextcloud/files";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
/*!
 * SPDX-FileCopyrightText: 2024 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
function getGuestNameValidity(name) {
  if (name.trim() === "") {
    return t("Names must not be empty.");
  }
  if (name.startsWith(".")) {
    return t("Names must not start with a dot.");
  }
  if (name.length > 64) {
    return t("Names may be at most 64 characters long.");
  }
  try {
    validateFilename(name);
    return "";
  } catch (error) {
    if (!(error instanceof InvalidFilenameError)) {
      throw error;
    }
    switch (error.reason) {
      case InvalidFilenameErrorReason.Character:
        return t('"{char}" is not allowed inside a name.', { char: error.segment });
      case InvalidFilenameErrorReason.ReservedName:
        return t('"{segment}" is a reserved name and not allowed.', { segment: error.segment });
      case InvalidFilenameErrorReason.Extension:
        if (error.segment.match(/\.[a-z]/i)) {
          return t('"{extension}" is not an allowed name.', { extension: error.segment });
        }
        return t('Names must not end with "{extension}".', { extension: error.segment });
      default:
        return t("Invalid name.");
    }
  }
}
const _hoisted_1 = {
  key: 0,
  class: "public-auth-prompt__text"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "PublicAuthPrompt",
  props: {
    nickname: { default: "" },
    title: { default: t("Guest identification") },
    text: { default: "" },
    notice: { default: "" },
    submitLabel: { default: t("Submit name") },
    cancellable: { type: Boolean }
  },
  emits: ["close"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const inputElement = useTemplateRef("input");
    const storage = getBuilder("public").build();
    const name = ref(props.nickname);
    watch(() => props.nickname, () => {
      name.value = props.nickname;
    });
    watch(name, (newName) => {
      const validity = getGuestNameValidity(newName);
      if (!validity) {
        inputElement.value.setCustomValidity(validity);
        inputElement.value.reportValidity();
        inputElement.value.focus();
        return;
      }
    });
    const buttons = computed(() => {
      const cancelButton = {
        label: t("Cancel"),
        variant: "tertiary",
        callback: () => emit("close")
      };
      const submitButton = {
        label: props.submitLabel,
        type: "submit",
        variant: "primary"
      };
      if (props.cancellable) {
        return [cancelButton, submitButton];
      }
      return [submitButton];
    });
    const defaultNotice = computed(() => {
      if (props.notice) {
        return props.notice;
      }
      if (name.value) {
        return t("You are currently identified as {nickname}.", { nickname: name.value });
      }
      return t("You are currently not identified.");
    });
    function onSubmit() {
      const nickname = name.value.trim();
      const validity = getGuestNameValidity(nickname);
      if (validity) {
        inputElement.value.setCustomValidity(validity);
        inputElement.value.reportValidity();
        inputElement.value.focus();
        return;
      }
      if (nickname === "") {
        inputElement.value.setCustomValidity(t("You cannot leave the name empty."));
        inputElement.value.reportValidity();
        inputElement.value.focus();
        return;
      }
      if (nickname.length < 2) {
        inputElement.value.setCustomValidity(t("Please enter a name with at least 2 characters."));
        inputElement.value.reportValidity();
        inputElement.value.focus();
        return;
      }
      try {
        setGuestNickname(nickname);
      } catch (error) {
        logger.error("Failed to set nickname", { error });
        showError(t("Failed to set nickname."));
        inputElement.value.focus();
        return;
      }
      storage.setItem("public-auth-prompt-shown", "true");
      emit("close", name.value);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(NcDialog), {
        buttons: buttons.value,
        class: "public-auth-prompt",
        "data-cy-public-auth-prompt-dialog": "",
        "is-form": "",
        "no-close": "",
        name: _ctx.title,
        onSubmit
      }, {
        default: withCtx(() => [
          _ctx.text ? (openBlock(), createElementBlock("p", _hoisted_1, toDisplayString(_ctx.text), 1)) : createCommentVNode("", true),
          createVNode(unref(NcNoteCard), {
            class: "public-auth-prompt__header",
            text: defaultNotice.value,
            type: "info"
          }, null, 8, ["text"]),
          createVNode(unref(NcTextField), {
            ref: "input",
            modelValue: name.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => name.value = $event),
            class: "public-auth-prompt__input",
            "data-cy-public-auth-prompt-dialog-name": "",
            label: unref(t)("Name"),
            placeholder: unref(t)("Enter your name"),
            required: !_ctx.cancellable,
            minlength: "2",
            maxlength: "64",
            name: "name"
          }, null, 8, ["modelValue", "label", "placeholder", "required"])
        ]),
        _: 1
      }, 8, ["buttons", "name"]);
    };
  }
});
const PublicAuthPrompt = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-b33d88e5"]]);
export {
  PublicAuthPrompt as default
};
//# sourceMappingURL=PublicAuthPrompt-DXYD3c5o.mjs.map
